/**
 * Bank-Import-Seite (Sprint 3, Feb 2026).
 *
 * Funktionsumfang:
 *  - Upload (CAMT.053 / CSV / PDF/Bild via KI)
 *  - Konten-Übersicht
 *  - Transaktions-Tabelle mit Match-Status-Filter
 *  - Match-Drawer pro Transaktion (Vorschläge + manuelle Suche, Ignorieren)
 *
 * Pro-Plan + DSGVO-Consent ist nur für den AI-Pfad relevant.
 * CAMT.053 + CSV laufen lokal ohne Consent-Anforderung.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/utils/api";
import {
  Banknote, Upload, RefreshCw, FileText, AlertTriangle,
  CheckCircle2, X, Sparkles, Eye, Trash2, Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";


const STATUS_LABELS = {
  unmatched: "Offen",
  matched: "Zugeordnet",
  ignored: "Ignoriert",
};

const STATUS_STYLES = {
  unmatched: "bg-amber-100 text-amber-800 border-amber-200",
  matched: "bg-emerald-100 text-emerald-800 border-emerald-200",
  ignored: "bg-slate-200 text-slate-600 border-slate-300",
};


function fmtAmount(value, currency = "EUR") {
  if (value === null || value === undefined) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${Number(value).toLocaleString("de-DE", { style: "currency", currency })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE");
  } catch {
    return iso;
  }
}


/** Counter-Tile — KPI-Mini-Card */
function CounterTile({ label, value, accent }) {
  const accentClass = {
    emerald: "border-l-emerald-500",
    amber: "border-l-amber-500",
    slate: "border-l-slate-400",
    blue: "border-l-blue-500",
  }[accent] || "border-l-slate-400";
  return (
    <div className={`bg-white rounded-lg border border-slate-200 border-l-4 ${accentClass} px-4 py-3`}>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}


/** Match-Drawer — Vorschläge + manuelle Auswahl */
function MatchDrawer({ open, onClose, transaction, onMatched }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !transaction) return;
    setLoading(true);
    api.get(`/bank/transactions/${transaction.id}/suggestions`)
      .then(res => setSuggestions(res.data.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, [open, transaction]);

  const handleMatch = async (s) => {
    try {
      await api.post(`/bank/transactions/${transaction.id}/match`, {
        type: s.type,
        target_id: s.id,
      });
      toast.success("Transaktion zugeordnet");
      onMatched?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Match fehlgeschlagen");
    }
  };

  const handleUnmatch = async () => {
    try {
      await api.post(`/bank/transactions/${transaction.id}/unmatch`);
      toast.info("Zuordnung aufgehoben");
      onMatched?.();
      onClose();
    } catch {
      toast.error("Aufhebung fehlgeschlagen");
    }
  };

  const handleIgnore = async () => {
    try {
      await api.post(`/bank/transactions/${transaction.id}/ignore`, { reason: "Manuell ausgeblendet" });
      toast.info("Transaktion ignoriert");
      onMatched?.();
      onClose();
    } catch {
      toast.error("Ignorieren fehlgeschlagen");
    }
  };

  if (!transaction) return null;
  const isCredit = transaction.amount > 0;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="bank-match-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-emerald-600" />
            Transaktion zuordnen
          </SheetTitle>
          <SheetDescription>
            Wähle eine passende Rechnung oder Ausgabe.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Transaction Info */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 uppercase tracking-wide">
                {fmtDate(transaction.booking_date)}
              </span>
              <Badge className={`${STATUS_STYLES[transaction.match_status]} border`}>
                {STATUS_LABELS[transaction.match_status]}
              </Badge>
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${isCredit ? "text-emerald-700" : "text-rose-700"}`}>
              {fmtAmount(transaction.amount, transaction.currency)}
            </div>
            <div className="text-sm text-slate-700">
              <span className="font-medium">{transaction.counterparty_name || "—"}</span>
              {transaction.counterparty_iban && (
                <span className="text-slate-500 ml-2 font-mono text-xs">{transaction.counterparty_iban}</span>
              )}
            </div>
            {transaction.purpose && (
              <div className="text-xs text-slate-600 italic line-clamp-2">{transaction.purpose}</div>
            )}
          </div>

          {/* Already matched? */}
          {transaction.match_status === "matched" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-xs text-emerald-700 uppercase font-medium mb-1">Aktuell zugeordnet</div>
              <div className="text-sm text-emerald-900">
                {transaction.matched_invoice
                  ? `Rechnung ${transaction.matched_invoice.invoice_number || transaction.matched_invoice.id}`
                  : transaction.matched_expense
                    ? `Ausgabe: ${transaction.matched_expense.vendor || transaction.matched_expense.description}`
                    : "Verknüpft"}
              </div>
              {transaction.match_reason && (
                <div className="text-xs text-emerald-700 mt-1">{transaction.match_reason}</div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="mt-3 border-emerald-300 hover:bg-emerald-100"
                onClick={handleUnmatch}
                data-testid="bank-unmatch-btn"
              >
                Zuordnung aufheben
              </Button>
            </div>
          )}

          {/* Suggestions */}
          {transaction.match_status !== "matched" && (
            <>
              <div>
                <h3 className="text-sm font-medium text-slate-900 mb-2">
                  Vorschläge ({suggestions.length})
                </h3>
                {loading ? (
                  <div className="text-sm text-slate-500 py-4 text-center">Lade Vorschläge…</div>
                ) : suggestions.length === 0 ? (
                  <div className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    Keine passenden {isCredit ? "Rechnungen" : "Ausgaben"} gefunden.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {suggestions.map((s) => (
                      <button
                        key={`${s.type}-${s.id}`}
                        onClick={() => handleMatch(s)}
                        className="w-full text-left rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 p-3 transition-colors"
                        data-testid={`bank-suggestion-${s.type}-${s.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-slate-900">{s.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {fmtDate(s.date)} · {fmtAmount(s.amount, transaction.currency)}
                            </div>
                            <div className="text-xs text-slate-600 mt-1 italic">{s.reason}</div>
                          </div>
                          <Badge variant="outline" className="ml-2 shrink-0">
                            {Math.round(s.confidence * 100)}%
                          </Badge>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleIgnore}
                  className="text-slate-600 hover:text-slate-900"
                  data-testid="bank-ignore-btn"
                >
                  <X className="h-4 w-4 mr-1.5" />
                  Diese Transaktion ignorieren
                </Button>
                <p className="text-xs text-slate-500 mt-1">
                  z.B. für Privatentnahmen oder Bankgebühren ohne Beleg.
                </p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}


export default function BankingPage() {
  const [accounts, setAccounts] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [filter, setFilter] = useState("unmatched");
  const [accountFilter, setAccountFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drawerTx, setDrawerTx] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  // Multi-File-Queue (Feb 2026): SB-Onboarding lädt häufig 4 Quartalsauszüge auf einmal hoch.
  // Pro Item: { name, status: 'pending'|'processing'|'done'|'error', imported?, auto_matched?, error? }
  const [uploadQueue, setUploadQueue] = useState([]);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // ── Constants für Format-Erkennung (geteilt zwischen Drop, File-Input und Multi-Queue) ─
  const ACCEPTED_EXT = ['.xml', '.csv', '.txt', '.pdf', '.jpg', '.jpeg', '.png', '.webp'];
  const AI_EXT = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, txRes] = await Promise.all([
        api.get("/bank/accounts"),
        api.get("/bank/transactions", {
          params: {
            // Filter (matched/unmatched/ignored) wird client-seitig angewendet,
            // damit die Counter auch dann stimmen wenn ein Filter aktiv ist.
            account_id: accountFilter === "all" ? undefined : accountFilter,
            limit: 500,
          },
        }),
      ]);
      setAccounts(accRes.data.accounts || []);
      setAllTransactions(txRes.data.transactions || []);
    } catch (e) {
      toast.error("Daten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [accountFilter]);

  useEffect(() => { reload(); }, [reload]);

  // Counts aus dem ungefilterten Array — sonst würde z.B. „Ignoriert (0)" stehen,
  // sobald der Filter „unmatched" aktiv ist (Bug aus Iter 70 Feedback).
  const counts = useMemo(() => {
    return allTransactions.reduce((acc, t) => {
      acc.total += 1;
      if (t.match_status === "matched") acc.matched += 1;
      else if (t.match_status === "ignored") acc.ignored += 1;
      else acc.unmatched += 1;
      return acc;
    }, { total: 0, matched: 0, unmatched: 0, ignored: 0 });
  }, [allTransactions]);

  // Display-Transaktionen werden client-seitig gefiltert.
  const transactions = useMemo(() => {
    if (filter === "all") return allTransactions;
    return allTransactions.filter(t => {
      if (filter === "matched") return t.match_status === "matched";
      if (filter === "ignored") return t.match_status === "ignored";
      // unmatched: weder matched noch ignored
      return t.match_status !== "matched" && t.match_status !== "ignored";
    });
  }, [allTransactions, filter]);

  // Single-File Wrapper bleibt, ruft intern handleFiles auf — File-Input hat
  // damit dieselbe Pipeline wie Drag&Drop.
  const handleFile = useCallback(async (file, useAi = false) => {
    if (!file) return;
    await handleFilesInternal([{ file, useAi }]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  // eslint-disable-next-line
  }, []);

  // Sequenzielle Multi-File-Verarbeitung mit per-Datei Statusanzeige.
  // Sequentiell statt parallel, damit Auto-Match-DB-Zugriffe nicht race-anfällig sind
  // und der Backend-Timeout pro Request klar gehalten wird.
  const handleFilesInternal = useCallback(async (items) => {
    if (!items || items.length === 0) return;
    setUploading(true);
    const queue = items.map(it => ({
      name: it.file.name,
      status: 'pending',
      imported: 0,
      auto_matched: 0,
      duplicates: 0,
      error: null,
    }));
    setUploadQueue(queue);

    let totalImported = 0;
    let totalMatched = 0;
    let totalErrors = 0;
    for (let i = 0; i < items.length; i++) {
      setUploadQueue(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'processing' } : it));
      try {
        const fd = new FormData();
        fd.append("file", items[i].file);
        fd.append("use_ai", String(items[i].useAi));
        const res = await api.post("/bank/import", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const { imported, duplicates_skipped, auto_match } = res.data;
        totalImported += imported || 0;
        totalMatched += auto_match?.matched || 0;
        setUploadQueue(prev => prev.map((it, idx) => idx === i ? {
          ...it,
          status: 'done',
          imported: imported || 0,
          duplicates: duplicates_skipped || 0,
          auto_matched: auto_match?.matched || 0,
        } : it));
      } catch (e) {
        totalErrors += 1;
        const msg = e.response?.data?.detail || 'Import fehlgeschlagen';
        setUploadQueue(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'error', error: msg } : it));
      }
    }

    setUploading(false);
    if (items.length > 1) {
      if (totalErrors === 0) {
        toast.success(`${items.length} Dateien verarbeitet · ${totalImported} Buchungen importiert · ${totalMatched} automatisch zugeordnet`);
      } else {
        toast.warning(`${items.length - totalErrors}/${items.length} Dateien OK · ${totalImported} Buchungen importiert · ${totalErrors} Fehler`);
      }
    }
    reload();
  }, [reload]);

  // Public: Drop-Handler & Multi-File-Picker rufen das hier auf.
  const handleFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const valid = [];
    const invalid = [];
    for (const f of Array.from(fileList)) {
      const lower = f.name.toLowerCase();
      if (ACCEPTED_EXT.some(ext => lower.endsWith(ext))) {
        valid.push({ file: f, useAi: AI_EXT.some(ext => lower.endsWith(ext)) });
      } else {
        invalid.push(f.name);
      }
    }
    if (invalid.length) {
      toast.error(`${invalid.length} Datei(en) ignoriert (Format nicht unterstützt): ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? ', …' : ''}`);
    }
    if (!valid.length) return;
    await handleFilesInternal(valid);
    if (fileInputRef.current) fileInputRef.current.value = "";
  // eslint-disable-next-line
  }, [handleFilesInternal]);

  // Drag & Drop (P2 Banking UX, Feb 2026): Überall im Fenster droppbar — auch Multi-File.
  // dragCounter-Pattern, weil dragleave bei Hover auf Child-Elementen feuert.
  useEffect(() => {
    const onDragEnter = (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragCounter.current += 1;
      setIsDragging(true);
    };
    const onDragOver = (e) => {
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    };
    const onDragLeave = (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setIsDragging(false);
    };
    const onDrop = (e) => {
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
      handleFiles(e.dataTransfer?.files);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [handleFiles]);

  const onPickFile = (e) => {
    handleFiles(e.target.files);
  };

  const triggerAutoMatch = async () => {
    try {
      const res = await api.post("/bank/auto-match");
      toast.success(`${res.data.matched} Transaktionen automatisch zugeordnet`);
      reload();
    } catch {
      toast.error("Auto-Match fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6" data-testid="banking-page">
      {/* P2 Banking UX (Feb 2026): Full-window Drag-Drop Overlay + Multi-File */}
      {isDragging && (
        <div
          className="fixed inset-0 z-50 bg-emerald-600/15 backdrop-blur-sm pointer-events-none flex items-center justify-center px-6"
          data-testid="banking-drop-overlay"
        >
          <div className="bg-white border-4 border-dashed border-emerald-500 rounded-2xl p-12 max-w-2xl w-full shadow-2xl">
            <Upload className="h-16 w-16 text-emerald-600 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-2xl font-bold text-emerald-900 mb-2 text-center">
              Kontoauszüge hier ablegen
            </h2>
            <p className="text-sm text-slate-600 text-center">
              Ein oder mehrere Dateien gleichzeitig — Format wird automatisch erkannt:
              CAMT.053 (XML), CSV, PDF/Bild (mit KI im Pro-Plan).
            </p>
          </div>
        </div>
      )}

      {/* Multi-File-Upload-Queue (sichtbar während/nach Verarbeitung) */}
      {uploadQueue.length > 0 && (
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="banking-upload-queue">
          <CardContent className="py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">
                Upload-Status ({uploadQueue.filter(q => q.status === 'done').length}/{uploadQueue.length} fertig)
              </div>
              {!uploading && (
                <button
                  onClick={() => setUploadQueue([])}
                  className="text-xs text-slate-500 hover:text-slate-800"
                  data-testid="banking-queue-clear-btn"
                >
                  Liste schließen
                </button>
              )}
            </div>
            <div className="space-y-1.5">
              {uploadQueue.map((q, idx) => {
                const StatusIcon = q.status === 'processing' ? Loader2
                  : q.status === 'done' ? CheckCircle2
                  : q.status === 'error' ? AlertTriangle
                  : Upload;
                const iconCls = q.status === 'processing' ? 'text-blue-600 animate-spin'
                  : q.status === 'done' ? 'text-emerald-600'
                  : q.status === 'error' ? 'text-rose-600'
                  : 'text-slate-400';
                return (
                  <div key={idx} className="flex items-center gap-2.5 text-xs bg-white rounded border border-slate-200 px-2.5 py-1.5"
                    data-testid={`banking-queue-item-${idx}`}>
                    <StatusIcon className={`h-3.5 w-3.5 shrink-0 ${iconCls}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{q.name}</div>
                      {q.status === 'done' && (
                        <div className="text-[11px] text-slate-500">
                          {q.imported} Buchungen importiert
                          {q.duplicates > 0 ? ` · ${q.duplicates} doppelt` : ''}
                          {q.auto_matched > 0 ? ` · ${q.auto_matched} automatisch zugeordnet` : ''}
                        </div>
                      )}
                      {q.status === 'error' && (
                        <div className="text-[11px] text-rose-700">{q.error}</div>
                      )}
                      {q.status === 'pending' && (
                        <div className="text-[11px] text-slate-400">Wartet…</div>
                      )}
                      {q.status === 'processing' && (
                        <div className="text-[11px] text-blue-700">Wird verarbeitet…</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Banknote className="h-7 w-7 text-emerald-600" />
            Banking
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Kontoauszüge importieren, Buchungen automatisch zu Rechnungen & Ausgaben zuordnen.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={triggerAutoMatch}
            disabled={loading || counts.unmatched === 0}
            data-testid="banking-auto-match-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Auto-Match
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="banking-upload-btn"
          >
            <Upload className="h-4 w-4 mr-1.5" />
            {uploading ? "Lädt…" : "Auszug hochladen"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".xml,.csv,.txt,.pdf,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={onPickFile}
            data-testid="banking-file-input"
          />
        </div>
      </div>

      {/* Format Hint */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="py-3 flex items-start gap-3">
          <FileText className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900">
            <span className="font-medium">Unterstützte Formate:</span>{" "}
            CAMT.053 (XML, alle DACH-Banken) · CSV (Sparkasse, DKB, Volksbank, N26, Erste/Bawag) · PDF/Bild (mit{" "}
            <Sparkles className="h-3 w-3 inline" /> KI im Pro-Plan).{" "}
            <Link to="/settings?section=banking-ai" className="underline hover:text-blue-700">KI-Einstellungen anpassen</Link>
          </div>
        </CardContent>
      </Card>

      {/* Sichtbare Dropzone (P4 Feedback Iter 71) — auch ohne aktiven Drag sichtbar */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className={`w-full border-2 border-dashed rounded-xl p-6 transition-all flex flex-col items-center justify-center gap-2 ${
          isDragging
            ? 'border-emerald-500 bg-emerald-50'
            : 'border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/30'
        }`}
        data-testid="banking-dropzone"
        disabled={uploading}
      >
        <Upload className={`h-7 w-7 ${isDragging ? 'text-emerald-600' : 'text-slate-400'}`} strokeWidth={1.7} />
        <div className="text-sm font-semibold text-slate-800">
          {isDragging ? 'Loslassen zum Hochladen' : 'Datei hier ablegen oder klicken zum Auswählen'}
        </div>
        <div className="text-xs text-slate-500">
          Eine oder mehrere Dateien · CAMT.053 / CSV / PDF / Bild — Format wird automatisch erkannt
        </div>
      </button>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CounterTile label="Gesamt" value={counts.total} accent="slate" />
        <CounterTile label="Offen" value={counts.unmatched} accent="amber" />
        <CounterTile label="Zugeordnet" value={counts.matched} accent="emerald" />
        <CounterTile label="Ignoriert" value={counts.ignored} accent="slate" />
      </div>

      {/* Accounts list */}
      {accounts.length > 0 && (
        <Card data-testid="banking-accounts-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Bankverbindungen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setAccountFilter("all")}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  accountFilter === "all"
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}
                data-testid="banking-acc-filter-all"
              >
                Alle ({accounts.length})
              </button>
              {accounts.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setAccountFilter(a.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    accountFilter === a.id
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`banking-acc-filter-${a.id}`}
                >
                  {a.bank_name || "Bankkonto"} · {a.iban?.slice(-4) || "—"}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={setFilter} className="w-full">
        <TabsList className="bg-slate-100" data-testid="banking-filter-tabs">
          <TabsTrigger value="unmatched" data-testid="banking-filter-unmatched">
            Offen ({counts.unmatched})
          </TabsTrigger>
          <TabsTrigger value="matched" data-testid="banking-filter-matched">
            Zugeordnet ({counts.matched})
          </TabsTrigger>
          <TabsTrigger value="ignored" data-testid="banking-filter-ignored">
            Ignoriert ({counts.ignored})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="banking-filter-all">
            Alle ({counts.total})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Transactions Table */}
      <Card>
        <CardContent className="p-0">
          {transactions.length === 0 ? (
            <div className="py-12 px-6 text-center max-w-2xl mx-auto" data-testid="banking-empty-state">
              <Banknote className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700">
                {loading ? "Lade Buchungen…" : "Noch keine Buchungen importiert."}
              </p>
              {!loading && (
                <>
                  <p className="text-xs text-slate-500 mt-1.5">
                    Lade deinen ersten Kontoauszug hoch (CAMT.053, CSV oder PDF).
                  </p>
                  {/* P2 Empty-State (Feb 2026): Erklärt den Workflow */}
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-1">
                        <span className="bg-emerald-100 text-emerald-700 rounded-full h-5 w-5 flex items-center justify-center text-[10px] font-bold">1</span>
                        Hochladen
                      </div>
                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        Deine Bank exportiert CAMT.053 oder CSV. PDF/Bild geht mit KI im Pro-Plan.
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-1">
                        <span className="bg-emerald-100 text-emerald-700 rounded-full h-5 w-5 flex items-center justify-center text-[10px] font-bold">2</span>
                        Auto-Match
                      </div>
                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        Buchungen werden automatisch zu deinen Rechnungen & Ausgaben zugeordnet (Betrag + Datum + IBAN/Verwendungszweck).
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-1">
                        <span className="bg-emerald-100 text-emerald-700 rounded-full h-5 w-5 flex items-center justify-center text-[10px] font-bold">3</span>
                        Dokumente
                      </div>
                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        Originaldatei landet automatisch im Dokumente-Bereich unter „Kontoauszüge" — GoBD-konform 10 Jahre archiviert.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="banking-tx-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left py-2.5 px-3">Datum</th>
                    <th className="text-left py-2.5 px-3">Empfänger / Auftraggeber</th>
                    <th className="text-left py-2.5 px-3">Verwendungszweck</th>
                    <th className="text-right py-2.5 px-3">Betrag</th>
                    <th className="text-left py-2.5 px-3">Status</th>
                    <th className="text-right py-2.5 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => {
                    const isCredit = t.amount > 0;
                    return (
                      <tr
                        key={t.id}
                        className="border-b border-slate-100 hover:bg-slate-50/50"
                        data-testid={`banking-tx-row-${t.id}`}
                      >
                        <td className="py-2.5 px-3 text-slate-600 tabular-nums">
                          {fmtDate(t.booking_date)}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="font-medium text-slate-900 line-clamp-1">
                            {t.counterparty_name || <span className="text-slate-400 italic">unbekannt</span>}
                          </div>
                          {t.counterparty_iban && (
                            <div className="text-xs text-slate-400 font-mono">{t.counterparty_iban}</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 max-w-xs">
                          <div className="line-clamp-1">{t.purpose || "—"}</div>
                          {t.match_status === "matched" && (
                            <div className="text-xs text-emerald-700 mt-0.5 line-clamp-1">
                              <CheckCircle2 className="h-3 w-3 inline mr-1" />
                              {t.matched_invoice
                                ? `Rg. ${t.matched_invoice.invoice_number || t.matched_invoice.id}`
                                : t.matched_expense
                                  ? `Ausg.: ${t.matched_expense.vendor || t.matched_expense.description}`
                                  : t.match_reason}
                            </div>
                          )}
                        </td>
                        <td
                          className={`py-2.5 px-3 text-right font-mono tabular-nums font-medium ${
                            isCredit ? "text-emerald-700" : "text-rose-700"
                          }`}
                        >
                          {fmtAmount(t.amount, t.currency)}
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={`${STATUS_STYLES[t.match_status]} border text-xs`}>
                            {STATUS_LABELS[t.match_status]}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDrawerTx(t)}
                            data-testid={`banking-tx-open-${t.id}`}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            Öffnen
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <MatchDrawer
        open={!!drawerTx}
        onClose={() => setDrawerTx(null)}
        transaction={drawerTx}
        onMatched={reload}
      />
    </div>
  );
}
